// Uhrzeit zum Eintippen UND zum Anklicken (19.10.2026, Dilsar/Simon): EINE Komponente für jedes Uhrzeitfeld im CRM.
// Klick ins Feld klappt sofort eine Liste auf (07:00 bis 20:00 in 15-Minuten-Schritten wie in Close), gescrollt zur
// eingetragenen bzw. aktuellen Uhrzeit. Tippen springt in der Liste zur passenden Zeit („17“ → 17:00, „1730“ → 17:30),
// Pfeiltasten bewegen die Markierung, Klick auf einen Eintrag übernimmt ihn, Enter übernimmt die getippte bzw. markierte Zeit.
// Erkennung: werkzeuge/zeitLesen.ts („15“, „1500“, „930“, „1530“, „15:30“, „15.30“). Ungültiges („25“, „1575“) bleibt rot
// markiert stehen, ohne Pop-up, und wird NIE übernommen: der Wert davor bleibt, `onUngueltig` meldet es dem Formular.
// Nach außen geht nur eine gültige Uhrzeit („HH:MM“) oder "" (leer, wenn leer erlaubt ist).
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { berlinTeile } from "../werkzeuge/zeit";
import { zeitBewerten, zeitLesen, zeitMarkierung, zeitOptionen, zeitSchritt, zeitStart } from "../werkzeuge/zeitLesen";
import { Ausklapp } from "./Ausklapp";

export { zeitLesen };

interface Props {
  wert: string;
  onChange: (zeit: string) => void;
  /** Enter mit gültiger Uhrzeit (sonst greift ein umgebendes Formular wie gewohnt) */
  onEnter?: (zeit: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** meldet, ob der getippte Text gerade ungültig ist (Formulare sperren damit „Speichern“) */
  onUngueltig?: (ungueltig: boolean) => void;
  className?: string;
  ariaLabel?: string;
  title?: string;
  autoFocus?: boolean;
  /** false: das Feld darf nicht leer werden (Sendefenster, Wählzeiten) */
  leerErlaubt?: boolean;
  /** Grund, wenn eine gültige Uhrzeit nicht passt (z. B. außerhalb des erlaubten Rahmens); solche Zeiten fehlen auch in der Liste */
  pruefen?: (zeit: string) => string | null;
  /** true (Standard): gültige Eingabe geht sofort nach außen; false: erst bei Enter oder Verlassen (Felder, die gleich speichern) */
  sofort?: boolean;
  /** bei ungültigem Text: "zurueck" (Standard) = Wert vor dem Tippen, "leer" = leer melden */
  beiUngueltig?: "zurueck" | "leer";
  disabled?: boolean;
  /** Rahmen und Raster der Auswahlliste (Standard 07:00 bis 20:00 in 15 Minuten) */
  listeVon?: string;
  listeBis?: string;
  schritt?: number;
}

export function ZeitFeld({
  wert,
  onChange,
  onEnter,
  onKeyDown,
  onUngueltig,
  className = "eingabe",
  ariaLabel = "Uhrzeit",
  title,
  autoFocus,
  leerErlaubt = true,
  pruefen,
  sofort = true,
  beiUngueltig = "zurueck",
  disabled,
  listeVon = "07:00",
  listeBis = "20:00",
  schritt = 15,
}: Props) {
  const [text, setText] = useState(wert);
  const [fokus, setFokus] = useState(false);
  const [versucht, setVersucht] = useState(false);
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState<string | null>(null);
  const [anker, setAnker] = useState<HTMLInputElement | null>(null);
  const liste = useRef<HTMLDivElement | null>(null);
  // zuletzt nach außen gemeldeter bzw. beim Anklicken vorhandener Wert
  const zuletzt = useRef(wert);
  useEffect(() => {
    zuletzt.current = wert;
    if (!fokus) setText(wert);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wert]);

  const b = zeitBewerten(text, { leerErlaubt, pruefen });
  const fehler = !b.ok;
  const melden = useRef(onUngueltig);
  melden.current = onUngueltig;
  useEffect(() => {
    melden.current?.(fehler);
  }, [fehler]);
  useEffect(() => () => melden.current?.(false), []);

  const geben = (z: string) => {
    if (z === zuletzt.current) return;
    zuletzt.current = z;
    onChange(z);
  };
  const startWert = useRef(wert);
  const rot = fehler && (!fokus || versucht);

  const optionen = useMemo(
    () => zeitOptionen(listeVon, listeBis, schritt, zeitLesen(wert) ? [zeitLesen(wert) as string] : []).filter((z) => !pruefen?.(z)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listeVon, listeBis, schritt, wert],
  );

  const aufklappen = () => {
    if (disabled) return;
    if (!offen) setMarkiert(zeitStart(optionen, text, berlinTeile(new Date().toISOString()).zeit));
    setOffen(true);
  };

  // zur Markierung scrollen: beim Aufklappen mittig, danach nur so weit wie nötig
  const zuerst = useRef(true);
  useEffect(() => {
    if (!offen) {
      zuerst.current = true;
      return;
    }
    const kasten = liste.current;
    const el = markiert ? kasten?.querySelector<HTMLElement>(`[data-zeit="${markiert}"]`) : null;
    if (!kasten || !el) return;
    if (zuerst.current) {
      kasten.scrollTop = el.offsetTop - kasten.clientHeight / 2 + el.offsetHeight / 2;
      zuerst.current = false;
    } else {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [offen, markiert, optionen]);

  const uebernehmen = (z: string) => {
    startWert.current = z;
    setText(z);
    setVersucht(false);
    geben(z);
  };

  return (
    <>
      <input
        ref={setAnker}
        className={`${className}${rot ? " eingabe--fehler zeitfeld--ungueltig" : ""}`}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="hh:mm"
        size={6}
        value={text}
        autoFocus={autoFocus}
        disabled={disabled}
        role="combobox"
        aria-expanded={offen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-invalid={fehler}
        title={rot && b.grund ? b.grund : (title ?? "Uhrzeit eintippen (z. B. 15 oder 15:30) oder aus der Liste wählen")}
        onFocus={(e) => {
          setFokus(true);
          startWert.current = zuletzt.current;
          e.currentTarget.select();
          aufklappen();
        }}
        onClick={aufklappen}
        onBlur={() => {
          setFokus(false);
          setVersucht(false);
          setOffen(false);
          if (b.ok) {
            setText(b.zeit);
            geben(b.zeit);
          }
          // ungültig: Text bleibt stehen (rot), der Wert davor gilt weiter
        }}
        onChange={(e) => {
          const neu = e.target.value;
          setText(neu);
          setVersucht(false);
          setOffen(true);
          const x = zeitBewerten(neu, { leerErlaubt, pruefen });
          if (x.ok) {
            setMarkiert(x.zeit ? zeitMarkierung(optionen, x.zeit) : null);
            if (sofort) geben(x.zeit);
          } else {
            const z = zeitLesen(neu);
            if (z) setMarkiert(zeitMarkierung(optionen, z));
            if (sofort) geben(beiUngueltig === "leer" ? "" : startWert.current);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const z = zeitLesen(text);
            const ausgang = offen ? (markiert ?? (z ? zeitMarkierung(optionen, z) : null)) : null;
            const neu = offen ? zeitSchritt(optionen, ausgang, e.key === "ArrowDown" ? 1 : -1) : zeitStart(optionen, text, berlinTeile(new Date().toISOString()).zeit);
            setOffen(true);
            if (neu) {
              setMarkiert(neu);
              if (offen) {
                setText(neu);
                if (sofort) geben(neu);
              }
            }
            return;
          }
          if (e.key === "Enter") {
            if (!b.ok) {
              e.preventDefault();
              setVersucht(true);
              return;
            }
            geben(b.zeit);
            startWert.current = b.zeit;
            setText(b.zeit);
            setOffen(false);
            if (onEnter) {
              e.preventDefault();
              onEnter(b.zeit);
              return;
            }
          }
          onKeyDown?.(e);
        }}
      />
      <Ausklapp anker={anker} offen={offen && optionen.length > 0} onSchliessen={() => setOffen(false)} breite="anker" minBreite={92} maxHoehe={232} rolle="listbox" className="zeitliste-ausklapp ausklapp--ueber-dialog">
        <div
          ref={liste}
          className="zeitliste"
          role="presentation"
          // der Fokus bleibt im Feld: ein Klick in die Liste darf es nicht verlassen
          onMouseDown={(e) => e.preventDefault()}
        >
          {optionen.map((z) => (
            <button
              key={z}
              type="button"
              tabIndex={-1}
              role="option"
              aria-selected={z === markiert}
              data-zeit={z}
              className={`zeitliste__eintrag${z === markiert ? " zeitliste__eintrag--markiert" : ""}${z === wert ? " zeitliste__eintrag--aktuell" : ""}`}
              onClick={() => {
                uebernehmen(z);
                setOffen(false);
              }}
            >
              {z}
            </button>
          ))}
        </div>
      </Ausklapp>
    </>
  );
}
